import type { QueryClient } from '@tanstack/react-query';

/* Cache plumbing for "create it inline, then pick it" flows.
 *
 * Two things used to go wrong when you added a category / team / org /
 * contact from inside a ticket form:
 *
 *  1. The invalidate only touched the one query key that screen used, so
 *     every OTHER picker and list still showed the old options.
 *  2. Even on the right key, the refetch lands a tick after we set the
 *     select's value — for that tick the <select> has a value with no
 *     matching <option>, so the browser resets it and the new record looks
 *     like it never saved.
 *
 * `addCreatedToCache` fixes both: it splices the record into every cached
 * list synchronously (so the <option> exists in the same render that
 * selects it) and then revalidates against the server.
 */

type WithId = { id?: string };

/** Queries whose first key segment starts with `prefix`. Our keys are flat
 *  strings like 'crm-orgs-picker', so a string prefix is the reliable way to
 *  hit every variant — react-query's own prefix match compares whole
 *  segments and would miss them. */
function keyStartsWith(queryKey: readonly unknown[], prefix: string): boolean {
  const head = queryKey[0];
  return typeof head === 'string' && head.startsWith(prefix);
}

function spliceIn(data: unknown, item: WithId, listField?: string): unknown {
  if (Array.isArray(data)) {
    return data.some((x: WithId) => x?.id === item.id) ? data : [item, ...data];
  }
  if (listField && data && typeof data === 'object') {
    const obj = data as Record<string, unknown>;
    const list = obj[listField];
    if (!Array.isArray(list)) return data;
    if (list.some((x: WithId) => x?.id === item.id)) return data;
    return { ...obj, [listField]: [item, ...list] };
  }
  return data;
}

/**
 * Make a just-created record visible everywhere it belongs, right now.
 *
 * @param keyPrefix   first key segment prefix of the lists to patch,
 *                    e.g. 'categories', 'crm-orgs', 'crm-contacts'
 * @param listField   field holding the array for paginated payloads
 *                    (`{ organizations: [...] }`); omit for plain arrays
 */
export function addCreatedToCache(
  queryClient: QueryClient,
  opts: { keyPrefix: string; item: WithId; listField?: string },
): void {
  const { keyPrefix, item, listField } = opts;
  if (!item?.id) return;

  queryClient.setQueriesData(
    { predicate: q => keyStartsWith(q.queryKey, keyPrefix) },
    (old: unknown) => (old === undefined ? old : spliceIn(old, item, listField)),
  );
  queryClient.invalidateQueries({ predicate: q => keyStartsWith(q.queryKey, keyPrefix) });
}

/** Every CRM query — list, picker, detail, board and dashboard alike. Any CRM
 *  write should call this so a contact added from a ticket form also shows up
 *  on the CRM screens (and vice versa). Relies on the `crm-*` key convention. */
export function invalidateCrm(queryClient: QueryClient): void {
  queryClient.invalidateQueries({ predicate: q => keyStartsWith(q.queryKey, 'crm-') });
}
